import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { ISSUES_MALWARE } from '../../../../shared/featureFlags/flagNames';
import { IssueFilterKeys, type QualityIssueType } from '../../../../shared/issuesV2';
import { useIsFeatureEnabled } from '../../../helpers/features';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useSetFilterHasChanged } from '../../../redux/savedFilters/hooks';
import { FilterPanel } from '../../generic/Filter';
import FilterCardCheckboxes from '../../generic/Filter/FilterCardCheckboxes';
import useFetchIssuesAction from '../IssuesInbox/hooks/useFetchIssuesAction';
import type { SelectOption } from '../../../types';
import type { SelectedOptionsProps } from '../../generic';

const BASE_FILTER_OPTIONS: SelectOption[] = [
  { value: 'outdated_dependency', label: 'Outdated version' },
  { value: 'blacklisted_dependency', label: 'Blocked package' },
  { value: 'risk_abandonware', label: 'Abandonware' },
  { value: 'risk_empty-package', label: 'Empty package' },
  { value: 'risk_native-code', label: 'Native code' },
];

const QualityIssueFilter: VoidFunctionComponent<Omit<SelectedOptionsProps, 'onSelect'>> = ({ selectedOptions }) => {
  const setFilters = useFetchIssuesAction(IssuesV2Store.actions.setFilter);
  const stateFilters = useSelector(IssuesV2Store.selectors.getFilter);
  const setFilterHasChanged = useSetFilterHasChanged();
  const isMalwareEnabled = useIsFeatureEnabled(ISSUES_MALWARE);

  const FILTER_OPTIONS = useMemo(() => {
    const options = [...BASE_FILTER_OPTIONS];
    if (isMalwareEnabled) {
      options.push({ value: 'malware', label: 'Malware' });
    }
    return options;
  }, [isMalwareEnabled]);

  const selectedCheckboxes = (selectedOptions ?? []).map((option: string) => ({
    label: FILTER_OPTIONS.find(filterOption => filterOption.value === option)?.label || option,
    value: option,
  }));

  const onCheckboxChange = (newSelected: SelectOption[]) => {
    setFilters({
      ...stateFilters,
      [IssueFilterKeys.Type]: newSelected.map(({ value }) => value as QualityIssueType),
    });
    setFilterHasChanged(true);
  };

  return (
    <FilterPanel>
      <FilterCardCheckboxes
        label="Issue type"
        options={FILTER_OPTIONS}
        values={selectedCheckboxes}
        onChange={onCheckboxChange}
        testId="issues-quality-filters"
      />
    </FilterPanel>
  );
};

export default QualityIssueFilter;
